import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import Button from "shared/components/Button";
import Select from "shared/components/Select";
import TextArea from "shared/components/TextArea";
import "shared/style/card.scss";
import "shared/style/typography.scss";
import RefreshSVG from "shared/icon/dashboard/Refresh.svg";
import appApi from "api";

/**
 * @typedef {{
 *  company: { label: string; value: string; };
 *  prompt: string;
 * }} AdminConfigurationFormData
 */

export default function AdminConfiguration() {
    const formMethods = useForm({
        defaultValues: {
            company: null,
            prompt: "",
        },
    });
    const { handleSubmit, setValue, watch } = formMethods;

    const [companyOptions, setCompanyOptions] = useState([]);

    /**
     * Calls the `onSubmitted` function on form submission if the form has no errors.
     * @param {AdminConfigurationFormData} data
     * @param {SubmitEvent} event
     */
    async function onSubmit(data, event) {
        event.preventDefault();

        try {
            await appApi.patch(`/company/recommender-prompt/${data.company.value}`, {
                recommender_prompt: data.prompt,
            });

            toast.success("Prompt has been updated");
        } catch (error) {
            // The form should handle so everything other than the internal error is just a precaution
            if (error.response?.status === 404) {
                toast.error(
                    "Company not found. This is most likely due to an internal error or the company was deleted after loading the page.",
                );
            } else if (error.response?.status === 400 && error.response.data.message?.match(/empty|blank/i)) {
                toast.error("The prompt cannot be empty");
            } else {
                toast.error("Failed to update the prompt");
            }
        }
    }

    async function resetRecommenderPromptToDefaultHandler() {
        try {
            const response = await appApi.patch(`/company/recommender-prompt/reset/${selectedCompany.value}`);
            setValue("prompt", response.data.recommender_prompt);

            toast.success("Prompt has been reset to default");
        } catch (error) {
            toast.error("Failed to reset the prompt to default");
        }
    }

    useEffect(() => {
        async function getCompanies() {
            /** @type {{ id: string; name: string; }[]} */
            const response = await appApi.get("/company", {
                params: {
                    field: ["id", "company_name"],
                    order_by: "company_name",
                },
            });
            const newCompanyOptions = response.data.map((company) => ({
                label: company.company_name,
                value: company.id,
            }));

            setCompanyOptions(newCompanyOptions);
        }

        getCompanies();
    }, []);

    // Watch for company changes and fetch the prompt
    const selectedCompany = watch("company");
    useEffect(() => {
        async function fetchRecommenderPrompt() {
            if (!selectedCompany?.value) {
                return;
            }

            try {
                const response = await appApi.get(`/company/recommender-prompt/${selectedCompany.value}`);
                setValue("prompt", response.data.prompt);
            } catch (error) {
                toast.error("Failed to fetch prompt for selected company");
            }
        }

        fetchRecommenderPrompt();
    }, [selectedCompany, setValue]); // Putting selectedCompany in deps array fetches prompt when company changes

    return (
        <div className="card" style={{ width: "calc(50% + 200px)", margin: "auto" }}>
            <FormProvider {...formMethods}>
                <div className="headline-6-semibold" style={{ color: "var(--colors-primary-slate-600)" }}>
                    Company Information
                </div>

                <form
                    style={{ display: "flex", flexDirection: "column", gap: "16px" }}
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Select
                        name="company"
                        label="Company"
                        options={companyOptions}
                        formOptions={{ required: "Company is required" }}
                    />

                    <TextArea
                        name="prompt"
                        label="Prompt"
                        style={{ width: "100%" }}
                        textAreaStyle={{ height: "400px" }}
                        formOptions={{ validate: (prompt) => prompt?.trim().length > 0 || "Prompt is required" }}
                    />

                    <div style={{ display: "flex", alignSelf: "flex-end", gap: "16px" }}>
                        <Button
                            type="button"
                            svg={RefreshSVG}
                            className="btn-secondary-gray"
                            onClick={resetRecommenderPromptToDefaultHandler}
                        >
                            Reset to default
                        </Button>

                        <Button type="submit" style={{ alignSelf: "flex-end" }}>
                            Update
                        </Button>
                    </div>
                </form>
            </FormProvider>
        </div>
    );
}
